import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import { UserManager } from "oidc-client";

import type { INewMessage } from "../../../../../resources-vue/vue-interfaces/messaging-service/i-message";
import type { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import type { IGetUserByName, IGetUserSlimInfoResult } from "../../../../../resources-vue/vue-interfaces/i-user";
import { MessageService } from "../../../../../services/message-service";
import { UserService } from "../../../../../services/user-service";
import { EnumsService } from "../../../../../services/enums.service";
import { EnumMap } from "../../../../../common/enum-map";
import { IGetUsersByNameQuery } from "../../../../../resources-vue/vue-interfaces/messaging-service/i-user";
import { ToastrService } from "../../../../../services/toastr.service";

import TagsInput from "../../../../../resources-vue/vue-custom-elements/TagsInput/TagsInput.vue";
import MessageInputHandler from "../../../../../resources-vue/vue-custom-elements/MessagingService/MessageInputHandler/MessageInputHandler.vue";

export default defineComponent({
    components: {
        TagsInput,
        MessageInputHandler
    },

    props: {
        closeSendMessage: {
            type: Function
        }
    },

    data() {
        return {
            _messageService: null,
            _usersService: null,
            _enumsService: null,
            _userManager: null,
            _toastrService: null,
            users: [] as ITypeaheadOptions[],
            currentUser: null as IGetUserSlimInfoResult,
            newMessage: {
                userIds: [],
                message: "",
                isImportant: false
            } as INewMessage,
            files: [] as File[],
            _activeStatus: null as number,
            isLoading: false
        };
    },

    async created() {
        this._messageService = Container.instance.get(MessageService);
        this._usersService = Container.instance.get(UserService);
        this._enumsService = Container.instance.get(EnumsService);
        this._userManager = Container.instance.get(UserManager);
        this._toastrService = Container.instance.get(ToastrService);
        const params = this.$route.params;
        this.$watch(
            () => this.$route.params,
            (toParams: any, previousParams: any) => {
                // react to route changes...
                if (previousParams !== toParams) {
                    return;
                }
            },
            { deep: true }
        );
        if (params.id) {
            let user = await this._usersService.getUserSlimInfo(params.id);
            if (user) {
                this.users.push({
                    name: `${user.lastName ? user.lastName + ", " : ""}${user.firstName}`,
                    value: params.id as string
                });
            }
        }
        let patientStatusEnum = await this._enumsService.getPatientStatusNew();
        let patientStatusEnumMap = new EnumMap(patientStatusEnum);
        this._activeStatus = patientStatusEnumMap.getEnumValue("active") as number;
    },

    watch: {
        users: {
            handler() {
                this.usersChanged();
            },
            deep: true
        }
    },

    methods: {
        async getUsersList(filter: string, limit: number) {
            const payload = {
                page: 1,
                pageLength: 25,
                term: filter.trim(),
                conversationParticipantUsers: []
            } as IGetUsersByNameQuery;
            const data = await this._messageService.getUserByName(payload);
            return this.getDisplayUsers(data.items);
        },

        getDisplayUsers(data: IGetUserByName[]) {
            if (data) {
                const filteredItems = data.filter((user) => !this.newMessage.userIds.includes(user.id));
                return filteredItems.map((user) => ({
                    name: `${user.lastName ? user.lastName + ", " : ""}${user.firstName}`,
                    value: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    application: user.application,
                    agencyName: user.agencyName,
                    agencyId: user.agencyId
                }));
            }
            return [];
        },

        usersChanged() {
            if (this.newMessage) {
                this.newMessage.userIds = this.users.map((user) => user.value);
            }
        },

        async sendMessage(value: any) {
            try {
                this.isLoading = true;
                this.newMessage.message = value.message;
                this.newMessage.assetIds = [];
                if (this.files?.length > 25) {
                    this.files.splice(25);
                }
                if (!!this.files?.length) {
                    this.newMessage.assetIds = await this._messageService.uploadMessageFiles(this.files);
                }
                const user = await this._userManager.getUser();
                this.newMessage.senderDisplayFirstname = user.profile.given_name;
                this.newMessage.senderDisplayLastname = user.profile.family_name;
                this.newMessage.placeHoldersList = [];
                await this._messageService.newConversation(this.newMessage);
                this.isLoading = false;
                this.closeSendMessage();
                this._toastrService.success({
                    title: `Message Sent`,
                    message: `Your message has been successfully sent.`
                });
            } catch (e) {
                console.error(e);
            }
        },

        async toggleImportant() {
            if (this.newMessage) {
                this.newMessage.isImportant = !this.newMessage.isImportant;
            }
        },

        updateTags(value: any) {
            this.users = value;
            if (this.users.length < 2) this.updateTitle(null);
        },

        updateTitle(value: string) {
            this.newMessage.title = value;
        }
    }
});
